import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApprove, useDeleteRecording, useReject, useReprocess } from "../api/recordings";
import { errorMessage } from "../api/client";
import ConfirmDialog from "./ConfirmDialog";
import type { RecordingDetail } from "../api/types";
import { STATUS_META } from "../lib/grade";
import { gateFailed, gateFailures, withoutMode } from "../lib/gate";
import { useIsDemo } from "../api/auth";

// Sticky bottom action bar: reviewer note + approve / reject / reprocess. Shows current
// decision/status and disables review actions outside pending_review. Also carries the
// hard-delete (DB row + bucket objects).
export default function ApproveBar({ rec }: { rec: RecordingDetail }) {
  const demo = useIsDemo();
  const [note, setNote] = useState("");
  const [moveObject, setMoveObject] = useState(false);
  const approve = useApprove(rec.id);
  const reject = useReject(rec.id);
  const reprocess = useReprocess(rec.id);
  const del = useDeleteRecording(rec.id);
  const navigate = useNavigate();

  const reviewable = rec.status === "pending_review";
  const busy =
    approve.isPending || reject.isPending || reprocess.isPending || del.isPending;
  const canReprocess = ["failed", "pending_review", "approved", "rejected", "superseded"].includes(rec.status);
  const body = { note: note || null, move_object: moveObject };
  const meta = STATUS_META[rec.status];

  // a gate-failed recording has no value as delivered: approving it must be a deliberate,
  // acknowledged override, never a stray click on the green button
  const failed = gateFailed(rec.qc);
  const [confirmingApprove, setConfirmingApprove] = useState(false);
  const onApprove = () => {
    if (failed) setConfirmingApprove(true);
    else approve.mutate(body);
  };

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const onDelete = () => {
    del.mutate(undefined, {
      onSuccess: () => navigate("/"),
      onSettled: () => setConfirmingDelete(false),
    });
  };

  const err =
    (approve.isError && errorMessage(approve.error)) ||
    (reject.isError && errorMessage(reject.error)) ||
    (reprocess.isError && errorMessage(reprocess.error)) ||
    (del.isError && errorMessage(del.error)) ||
    null;

  return (
    <div className="action-bar">
      <div className="action-bar-inner">
        <div className="action-left">
          <span className="status-badge" style={{ ["--sc" as string]: meta?.color ?? "#6e7681" }}>
            <span className="status-dot" />
            {meta?.label ?? rec.status}
          </span>
          {rec.decision && (
            <span className="muted small action-decision">
              {rec.decision.note ? `“${rec.decision.note}”` : "decision recorded"}
            </span>
          )}
          {reviewable && (
            <label className="move-toggle">
              <input
                type="checkbox"
                checked={moveObject}
                onChange={(e) => setMoveObject(e.target.checked)}
              />
              Move object
            </label>
          )}
        </div>

        {reviewable && (
          <input
            className="note-input"
            placeholder="Reviewer note (optional)…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        )}

        <div className="action-buttons">
          <button className="btn danger" disabled={busy} onClick={() => setConfirmingDelete(true)}>
            {del.isPending ? "Deleting…" : "Delete"}
          </button>
          {canReprocess && (
            <button className="btn ghost" disabled={busy} onClick={() => reprocess.mutate()}>
              {reprocess.isPending ? "Reprocessing…" : "Reprocess"}
            </button>
          )}
          <button
            className="btn reject"
            disabled={!reviewable || busy}
            onClick={() => reject.mutate(body)}
          >
            {reject.isPending ? "Rejecting…" : "Reject"}
          </button>
          <button
            className={failed ? "btn approve gate-failed" : "btn approve"}
            disabled={!reviewable || busy}
            onClick={onApprove}
            title={failed ? "This recording failed the QC gate" : undefined}
          >
            {approve.isPending ? "Approving…" : failed ? "Approve anyway…" : "Approve"}
          </button>
        </div>
      </div>
      {err && <p className="error action-error">{err}</p>}
      <ConfirmDialog
        open={confirmingApprove}
        title="Approve a recording that failed the QC gate?"
        confirmLabel={approve.isPending ? "Approving…" : "Approve anyway"}
        tone="danger"
        busy={approve.isPending}
        onConfirm={() => approve.mutate(body, { onSettled: () => setConfirmingApprove(false) })}
        onCancel={() => setConfirmingApprove(false)}
      >
        <p>
          The pipeline judged this recording <b>worthless as delivered</b>:
        </p>
        <ul>
          {gateFailures(rec.qc, rec.mode).map((r) => (demo ? withoutMode(r) : r)).map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
        <p>Approving counts it as paid, valid training data. Only do this if you understand why the gate is wrong here.</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirmingDelete}
        title="Permanently delete this recording?"
        confirmLabel={del.isPending ? "Deleting…" : "Delete recording"}
        tone="danger"
        busy={del.isPending}
        onConfirm={onDelete}
        onCancel={() => setConfirmingDelete(false)}
      >
        <p>
          The database entry <b>and</b> the stored video/artifacts (Oracle bucket + server
          files) will be removed.
        </p>
        <p>This cannot be undone.</p>
      </ConfirmDialog>
    </div>
  );
}
